import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from "react";
import { cn } from "@/shared/lib/cn";

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label: ReactNode;
  hideLabel?: boolean;
  description?: string;
  error?: string;
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(
    {
      label,
      hideLabel,
      description,
      error,
      indeterminate = false,
      className,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    forwardedRef,
  ) {
    const id = useId();
    const labelId = `${id}-label`;
    const descriptionId = description ? `${id}-description` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy =
      [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(" ") ||
      undefined;
    const localRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      if (localRef.current) localRef.current.indeterminate = indeterminate;
    }, [indeterminate]);
    const setRef = (node: HTMLInputElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };
    return (
      <label
        className={cn(
          "studio-choice",
          props.disabled && "cursor-not-allowed",
          className,
        )}
      >
        <input
          ref={setRef}
          type="checkbox"
          aria-labelledby={ariaLabelledBy ?? (props["aria-label"] ? undefined : labelId)}
          aria-describedby={describedBy}
          aria-invalid={error ? true : ariaInvalid}
          className="studio-checkbox"
          data-indeterminate={indeterminate || undefined}
          {...props}
        />
        <span className={cn("min-w-0 text-[13.5px] leading-[1.5] text-ink", hideLabel && "sr-only")}>
          <span id={labelId}>{label}</span>
          {description ? (
            <span id={descriptionId} className="block text-xs text-ink-muted">
              {description}
            </span>
          ) : null}
          {error ? (
            <span id={errorId} role="status" className="block text-xs text-danger-text">
              {error}
            </span>
          ) : null}
        </span>
      </label>
    );
  },
);

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: ReactNode;
  badge?: string;
}

export interface RadioGroupProps<T extends string> {
  name: string;
  value: T;
  options: RadioOption<T>[];
  onChange: (value: T) => void;
  label: string;
  error?: string;
  className?: string;
  variant?: "list" | "cards";
  disabled?: boolean;
}

export function RadioGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  label,
  error,
  className,
  variant = "list",
  disabled = false,
}: RadioGroupProps<T>) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  return (
    <fieldset
      className={cn("m-0 min-w-0 border-0 p-0", className)}
      aria-invalid={error ? true : undefined}
      aria-describedby={errorId}
      disabled={disabled}
    >
      <legend className="mb-1.5 text-[12.5px] font-medium tracking-[0.02em] text-ink">
        {label}
      </legend>
      <div className={cn("studio-radio-group", variant === "cards" && "studio-radio-cards")}>
        {options.map((option, index) => {
          const labelId = `${id}-option-${index}-label`;
          const descriptionId = option.description
            ? `${id}-option-${index}-description`
            : undefined;
          return (
            <label
              key={option.value}
              className={cn(
                "studio-choice",
                variant === "cards" && "studio-choice-card",
                (disabled || option.disabled) && "cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={option.value === value}
                disabled={option.disabled}
                aria-labelledby={labelId}
                aria-describedby={
                  [descriptionId, errorId].filter(Boolean).join(" ") ||
                  undefined
                }
                aria-invalid={error ? true : undefined}
                onChange={() => onChange(option.value)}
                className="studio-radio"
              />
              {option.icon ? <span className="studio-choice-icon" aria-hidden="true">{option.icon}</span> : null}
              <span
                className={cn(
                  "text-[13.5px] leading-[1.5] text-ink",
                  option.disabled && "text-ink-faint",
                )}
              >
                <span id={labelId}>{option.label}</span>
                {option.badge ? <span className="studio-choice-badge">{option.badge}</span> : null}
                {option.description ? (
                  <span
                    id={descriptionId}
                    className="block text-xs text-ink-muted"
                  >
                    {option.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p id={errorId} role="status" className="m-0 mt-1 text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label: string;
  description?: string;
  hideLabel?: boolean;
  controlSize?: "sm" | "md";
  tone?: "neutral" | "accent" | "image" | "video" | "audio";
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  {
    label,
    description,
    hideLabel = false,
    controlSize = "md",
    tone = "neutral",
    className,
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    ...props
  },
  ref,
) {
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = description ? `${id}-description` : undefined;
  return (
    <label
      className={cn(
        "studio-switch",
        props.disabled && "cursor-not-allowed",
        className,
      )}
      data-size={controlSize}
      data-tone={tone}
    >
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        aria-labelledby={ariaLabelledBy ?? (props["aria-label"] ? undefined : labelId)}
        aria-describedby={
          [ariaDescribedBy, descriptionId].filter(Boolean).join(" ") ||
          undefined
        }
        className="studio-switch-input sr-only"
        {...props}
      />
      <span aria-hidden="true" className="studio-switch-track">
        <span className="studio-switch-thumb" />
      </span>
      <span
        className={cn(
          "min-w-0 text-[13.5px] text-ink",
          props.disabled && "text-ink-faint",
          hideLabel && "sr-only",
        )}
      >
        <span id={labelId}>{label}</span>
        {description ? (
          <span
            id={descriptionId}
            className="block text-xs leading-[1.5] text-ink-muted"
          >
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
});


export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
  hideLabel?: boolean;
}

/** Native same-name radios for rich layouts with independent sibling actions. */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  function Radio({ label, description, hideLabel, className, "aria-describedby": describedBy, ...props }, ref) {
    const id = useId();
    return <label className={cn("studio-choice", className)}>
      <input ref={ref} type="radio" aria-labelledby={props["aria-label"] ? undefined : `${id}-label`}
        aria-describedby={[describedBy, description ? `${id}-description` : undefined].filter(Boolean).join(" ") || undefined}
        className="studio-radio" {...props} />
      <span className={cn("min-w-0 text-[13.5px] leading-relaxed text-ink", hideLabel && "sr-only")}>
        <span id={`${id}-label`}>{label}</span>
        {description ? <span id={`${id}-description`} className="block text-xs text-ink-muted">{description}</span> : null}
      </span>
    </label>;
  },
);
